import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';

import { ToastService } from '@services/common/toast.service';
import { UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { revealFormErrors } from 'app/main/helpers/form-validation';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { UserHttpService } from '@services/datasources/user-http.service';

export type FormulaireCallCompletion = 'userSuccess' | 'userFail' | 'passSuccess' | 'passFail';

interface UserGroupControls {
    lastname: FormControl<string | null>;
    firstname: FormControl<string | null>;
    role: FormControl<string | null>;
    email: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
}

interface SecurityGroupControls {
    oldPassword: FormControl<string | null>;
    newPassword: FormControl<string | null>;
    confirmPassword: FormControl<string | null>;
}

interface SettingsFormUserControls {
    userGroup: FormGroup<UserGroupControls>;
    securityGroup: FormGroup<SecurityGroupControls>;
}

@Component({
    selector: 'app-settings-form-user',
    templateUrl: './settings-form-user.component.html',
    styleUrls: ['./settings-form-user.component.scss'],
    standalone: false,
})
export class SettingsFormUserComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$!: Observable<QuestionnaireDto>;
    @Output() onboardedEvent = new EventEmitter<boolean>();

    public settingsForm!: FormGroup<SettingsFormUserControls>;

    private ngUnsubscribe = new Subject<void>();
    public userClaim?: UserClaims;
    public questionnaire?: QuestionnaireDto;
    public loading: boolean = false;

    public isAdminForm: boolean = false;

    constructor(
        private authService: CognitoAuthenticationService,
        private formBuilder: FormBuilder,
        private store: Store,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        private featureService: FeatureService,
        private userService: UserHttpService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.isAdminForm =
            this.featureService.hasUiFeatures(['organization_admin'], false) || this.featureService.hasSudoFeature();
        this.settingsForm = new FormGroup<SettingsFormUserControls>({
            userGroup: new FormGroup<UserGroupControls>({
                lastname: new FormControl<string | null>('', Validators.required),
                firstname: new FormControl<string | null>('', Validators.required),
                role: new FormControl<string | null>(''),
                email: new FormControl<string | null>({ value: '', disabled: true }, Validators.required),
                phoneNumber: new FormControl<string | null>('', CustomValidators.phoneNumberValidator),
            }),
            securityGroup: new FormGroup<SecurityGroupControls>(
                {
                    oldPassword: new FormControl<string | null>('', Validators.required),
                    newPassword: new FormControl<string | null>('', [
                        Validators.required,
                        CustomValidators.passwordValidator,
                    ]),
                    confirmPassword: new FormControl<string | null>('', Validators.required),
                },
                {
                    validators: [
                        CustomValidators.mustNotMatch('oldPassword', 'newPassword'),
                        CustomValidators.mustMatch('newPassword', 'confirmPassword'),
                    ],
                },
            ),
        });
    }

    get userFormGroup(): FormGroup<UserGroupControls> {
        return this.settingsForm.controls.userGroup;
    }

    get securityFormGroup(): FormGroup<SecurityGroupControls> {
        return this.settingsForm.controls.securityGroup;
    }

    ngOnInit(): void {
        this.authService.authenticatedUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (aU) => {
                const userFormGroup = this.userFormGroup;
                if (aU) {
                    const payload = aU as Record<string, string | undefined>;
                    userFormGroup.controls.lastname.setValue(payload['family_name'] ?? '');
                    userFormGroup.controls.firstname.setValue(payload['given_name'] ?? '');
                    userFormGroup.controls.role.setValue(payload['custom:role'] ?? '');
                    userFormGroup.controls.email.setValue(payload['email'] ?? '');
                    userFormGroup.controls.phoneNumber.setValue(payload['phone_number'] ?? '');
                } else {
                    userFormGroup.disable();
                }
            },
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onSubmit() {
        // The password fields only count once the user starts changing the password.
        const sections = [this.userFormGroup, ...(this.securityFormGroup.pristine ? [] : [this.securityFormGroup])];
        const valid = sections.map((section) => revealFormErrors(section, this.host.nativeElement)).every(Boolean);
        if (!valid) {
            return;
        }

        const userFormGroup = this.userFormGroup;
        const securityFormGroup = this.securityFormGroup;

        // here are initiated the collection and the subject to handle navigation
        // the signals are added in the array of signals to listen if the condition is met below

        const completionSignals: Subject<FormulaireCallCompletion>[] = [];
        let userInfoUpdateResult: Subject<FormulaireCallCompletion>;
        let passUpdateResult: Subject<FormulaireCallCompletion>;

        if (userFormGroup.valid && !userFormGroup.pristine) {
            userInfoUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(userInfoUpdateResult);

            const userControls = userFormGroup.controls;
            const user: UserInfoDto = {
                email: userControls.email.value ?? '',
                phone_number: CustomValidators.toE164Phone(userControls.phoneNumber.value),
                role: userControls.role.value ?? undefined,
                firstname: userControls.firstname.value ?? undefined,
                lastname: userControls.lastname.value ?? undefined,
                roles: [],
            };

            this.userService.updateCognitoUser(user).subscribe({
                next: () => {
                    this.authService.refreshAuthenticatedUser().subscribe({
                        next: () => {
                            userInfoUpdateResult.next('userSuccess');
                            userInfoUpdateResult.complete();
                            userFormGroup.markAsPristine();
                            this.toastService.showSuccess({
                                details: 'components.settingsForm.userSection.toasts.updateSuccess',
                            });
                        },
                        error: () => {
                            // Even if refresh fails, the update was successful
                            userInfoUpdateResult.next('userSuccess');
                            userInfoUpdateResult.complete();
                            userFormGroup.markAsPristine();
                            this.toastService.showSuccess({
                                details: 'components.settingsForm.userSection.toasts.updateSuccess',
                            });
                        },
                    });
                },
                error: () => {
                    userInfoUpdateResult.next('userFail');
                    userInfoUpdateResult.complete();
                    this.toastService.showError({
                        details: 'components.settingsForm.userSection.toasts.updateError',
                    });
                },
            });
        }
        if (securityFormGroup.valid && !securityFormGroup.pristine) {
            passUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(passUpdateResult);
            this.authService
                .changePassword(
                    securityFormGroup.controls.oldPassword.value ?? '',
                    securityFormGroup.controls.newPassword.value ?? '',
                )
                .subscribe({
                    //todo : message in page or in toast
                    next: () => {
                        securityFormGroup.markAsPristine();
                        passUpdateResult.next('passSuccess');
                        passUpdateResult.complete();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.securitySection.toasts.updateSuccess',
                        });
                    },
                    error: () => {
                        passUpdateResult.next('passFail');
                        passUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.securitySection.toasts.updateError',
                        });
                    },
                });
        }

        /**
         * all the api calls emit a completion signal with a type allowing to know what has success/failed errors and toast can be called before but need to know if everything succeded before navigation.
         * it's also possible to handle success/error from this callback
         *  if (completionSignals.length !== 0) {
         *      this.loading = true;
         *  }
         */
        combineLatest(completionSignals)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: () => {
                    this.loading = false;
                },
            });
    }

    /** Whether there is anything to save: submitting an untouched form would send nothing. */
    public hasChanges(): boolean {
        return !this.userFormGroup.pristine || !this.securityFormGroup.pristine;
    }
}
